import { inject, Injectable } from '@angular/core'
import { injectMutation, injectQuery, injectQueryClient } from './query'
import { HttpClient } from '@angular/common/http'
import { BehaviorSubject, map, of, switchMap, tap } from 'rxjs'
import { Meeting } from './meetings.service'
import { DemoProfileFormObject } from './support/demo-profiles/support-demo-profiles-page/support-demo-profiles-page.component'
import { Team } from './teams.service'
import { queryKeys } from './query-keys'
import { DownloadsService } from './downloads.service'
import { MetricDataPoint } from './metrics.service'
import { ListParameterQueryOptional } from './list-query-options'
import { ToastService } from './toast.service'
import { ResolvedSettings } from './client-settings'

export type UserRole = 'USER' | 'ADMIN' | 'SUPPORT'

export type CalendarProviderName = 'MICROSOFT' | 'GOOGLE' | string

export type UserCalendarProvider = {
  provider: CalendarProviderName
  email: string | null
}

export type DemoProfileDto = Omit<DemoProfileFormObject, 'meetings'> & { meetings: number[] }

export type User = {
  calendarProviders: UserCalendarProvider[]
  id: number
  teamId: number
  firstName: string
  lastName: string
  email: string
  username: string
  role: UserRole
  useUserDisplayName: boolean
  assistantDisplayName: string | null
  hasPassword: boolean
  isSensitiveModeEnabled: boolean
  isDarkModeEnabled: boolean
  timezone: string
  locale: string
  activatedAt: Date
  deactivatedAt: Date | null
  sendUpcomingMeetingReminderEmail: boolean | null
  sendWeeklyDigestEmail: boolean | null
  sendSoftDeletionWarningEmail: boolean | null
  sendSoftDeletionConfirmationEmail: boolean | null
  sendAssistantAccessDeniedEmail: boolean | null
  sendDeactivationEmail: boolean
  sendDeactivationWarningEmail: boolean
  notifyWhenPostProcessingCompletes: boolean
  hasActivationEmailBeenSent: boolean
  hasDeactivationEmailBeenSent: boolean
  hasDeactivationWarningEmailBeenSent: boolean
  tiptapEditorPollingFallbackActivatedAt: Date | null
  calendarSyncCompletedAt: Date | null
} & MeetingJoinSettings

export type MeetingJoinSettings = {
  joinMeetingTentativelyAccepted: boolean
  joinMeetingDeclined: boolean
  joinMeetingUnresponded: boolean
  joinMeetingInternal: boolean | null
  joinMeetingExternal: boolean | null
}

export type AdminUser = User & {
  createdAt: Date
  firstLoginAt: Date | null
  lastLoginAt: Date | null
  createdBy: string
  creationReason: string
  oidcProviders: AdminOidcProvider[]
  metricDataPoints: MetricDataPoint[]
  keycloakLink: string
  team: Team
}

export type UserWithTeam = User & {
  team: Team & {
    // The effective configuration; the team's own columns are only overrides.
    resolvedSettings: ResolvedSettings
  }
}

export type MeUser = UserWithTeam & {
  intercomAppId: string | null
  intercomHash: string | null
  environment: string
}

export type DemoProfile = {
  id: number
  name: string
  userId: number
  meetings: Meeting[]
}

export type AdminListUser = User & {
  team: Team
  nextMeetingAt: Date | null
  previousMeetingAt: Date | null
  lastLoginAt: Date | null
  firstLoginAt: Date | null
  lastActiveAt: Date | null
  oidcProviders: AdminOidcProvider[]
  isActive: boolean
}

export type AdminOidcProvider = {
  calendarPushNotificationRegistrationFailureCount: number
  calendarSynchronizationFailureCount: number
  failedPreviouslyDueToInsufficientPermissions: boolean
  lastRegisteredForNotificationsAt: Date | null
  provider: string
}

export type E2eTestUserOfflineToken = {
  email: string
  userExists: boolean
  offlineToken: string | null
  expiresAt: string | null
}

export type UserQueryResult = {
  list: AdminListUser[]
  total: number
}

export type UserToBeCreated = Omit<User, 'id' | 'username' | 'tiptapEditorPollingFallbackActivatedAt' | 'calendarSyncCompletedAt' | 'isDarkModeEnabled'> & {
  username: string | null
}

export type UserEditFormOutput = {
  user: User
}

export type SetUserActivation = {
  userIds: number[]
  active: boolean
}

export type UserCreateFormOutput = {
  user: UserToBeCreated
}

export type AdminUserListQueryFilters = {
  teamId?: number
  isActive?: boolean
  role?: UserRole
  isCalendarSynchronizationOk?: boolean
  startDate?: string
  endDate?: string
  hasLastActiveAt?: boolean
}
export type AdminUserListQuery = AdminUserListQueryFilters & ListParameterQueryOptional

@Injectable({
  providedIn: 'root',
})
export class UsersService {
  mutation = injectMutation()
  httpClient = inject(HttpClient)
  query = injectQuery()
  queryClient = injectQueryClient()
  toastService = inject(ToastService)
  currentUser$ = new BehaviorSubject<MeUser | null>(null)
  downloadsService = inject(DownloadsService)

  getUsersAsAdmin(params: AdminUserListQuery) {
    return this.query({
      queryKey: queryKeys.users.base,
      queryFn: () => this.httpClient.get<UserQueryResult>('/api/users/support', { params }),
    }).result$
  }

  getE2eTestUserOfflineToken() {
    return this.query({
      queryKey: queryKeys.users.e2eTestUserOfflineToken,
      queryFn: () => this.httpClient.get<E2eTestUserOfflineToken>('/api/users/e2e-test-user/offline-token'),
      staleTime: 0,
      gcTime: 0,
    }).result$
  }

  searchUsers(searchQuery: string) {
    return this.httpClient.get<UserQueryResult>('/api/users/support', {
      params: { query: searchQuery, pageSize: 20 },
    })
  }

  getUserAsAdmin(id: number, receiveUpdates = false) {
    return this.query({
      queryKey: queryKeys.users.byId(id),
      queryFn: () => this.httpClient.get<AdminUser>(`/api/users/${id}/support`),
      refetchOnMount: receiveUpdates,
      refetchOnReconnect: receiveUpdates,
      refetchOnWindowFocus: receiveUpdates,
    }).result$
  }

  getTeamUsers() {
    return this.query({
      queryKey: queryKeys.teams.users,
      queryFn: () => this.httpClient.get<User[]>(`/api/users/me/teams-users`),
    }).result$
  }

  getClientUsers() {
    return this.query({
      queryKey: queryKeys.clients.users,
      queryFn: () => this.httpClient.get<User[]>(`/api/users/me/client-users`),
    }).result$
  }

  /** The active users of every team of a client, who are the people its meetings can be given to. */
  getUsersByClientId(clientId: number) {
    return this.query({
      queryKey: queryKeys.users.byClientId(clientId),
      queryFn: () =>
        this.httpClient.get<{ id: number }[]>('/api/teams', { params: { clientIds: [clientId] } }).pipe(
          switchMap((teams) =>
            // An empty list of teams would leave the user list unfiltered, so a client without any is answered here.
            teams.length === 0
              ? of<UserQueryResult>({ list: [], total: 0 })
              : this.httpClient.get<UserQueryResult>('/api/users/support', {
                  params: { teamIds: teams.map((team) => team.id), isActive: true, pageSize: 1000 },
                })
          )
        ),
    }).result$
  }

  createUser() {
    return this.mutation({
      mutationFn: (user: UserCreateFormOutput) => this.httpClient.post<User>('/api/users', user),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: queryKeys.users.base }),
    })
  }

  updateUser() {
    return this.mutation({
      mutationFn: (user: UserEditFormOutput) => this.httpClient.patch<User>(`/api/users/${user.user.id}`, user),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: queryKeys.users.base }),
    })
  }

  deleteUser() {
    return this.mutation({
      mutationFn: (userId: number) => this.httpClient.delete<void>(`/api/users/${userId}`),
      onSuccess: (_, userId) => {
        // Dropped rather than left to the invalidation below, which matches it by prefix: a page still
        // open on the deleted user would refetch a row that is gone.
        this.queryClient.removeQueries({ queryKey: queryKeys.users.byId(userId), exact: true })
        return this.queryClient.invalidateQueries({ queryKey: queryKeys.users.base })
      },
    })
  }

  setUserActivation() {
    return this.mutation({
      mutationFn: (setUserActivation: SetUserActivation) => this.httpClient.patch<void>('/api/users/activation', setUserActivation),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: queryKeys.users.base }),
      onError: () => {
        this.toastService.show('USERS.ADMIN.ACTIVATION.UPDATE_FAILED')
      },
    })
  }

  getCurrentUser(skipRetries = false) {
    return this.query({
      queryKey: queryKeys.users.me,
      queryFn: () => this.fetchCurrentUser(),
      retry: skipRetries ? 0 : 3,
    }).result$
  }

  fetchCurrentUser() {
    return this.httpClient.get<MeUser>('/api/users/me').pipe(tap((user) => this.currentUser$.next(user)))
  }

  setToUsePasswordMutation() {
    return this.mutation({
      mutationFn: (userId: number) => this.httpClient.post<void>(`/api/users/${userId}/set-to-use-password`, {}),
      onSuccess: (_, userId) => this.queryClient.invalidateQueries({ queryKey: queryKeys.users.byId(userId) }),
    })
  }

  setToNotUsePasswordMutation() {
    return this.mutation({
      mutationFn: (userId: number) => this.httpClient.post<void>(`/api/users/${userId}/set-not-to-use-password`, {}),
      onSuccess: (_, userId) => this.queryClient.invalidateQueries({ queryKey: queryKeys.users.byId(userId) }),
    })
  }

  setSensitiveModeEnabledMutation() {
    return this.mutation({
      mutationFn: (params: { userId: number; isSensitiveModeEnabled: boolean }) =>
        this.httpClient.post<void>(`/api/users/${params.userId}/set-sensitive-mode-enabled`, {
          isSensitiveModeEnabled: params.isSensitiveModeEnabled,
        }),
      onSuccess: (_, params) => this.queryClient.invalidateQueries({ queryKey: queryKeys.users.byId(params.userId) }),
    })
  }

  setDarkModeEnabledMutation() {
    return this.mutation({
      mutationFn: (isDarkModeEnabled: boolean) => this.httpClient.post<void>('/api/users/me/set-dark-mode-enabled', { isDarkModeEnabled }),
      onSuccess: (_, isDarkModeEnabled) => {
        const currentUser = this.currentUser$.value
        if (currentUser !== null) {
          this.currentUser$.next({ ...currentUser, isDarkModeEnabled })
        }
        return this.queryClient.invalidateQueries({ queryKey: queryKeys.users.me })
      },
    })
  }

  setCalendarSynchronizationFailureCountMutation() {
    return this.mutation({
      mutationFn: (params: { userId: number; failureCount: number }) =>
        this.httpClient.post<void>(`/api/users/${params.userId}/set-calendar-synchronization-failure-count`, { failureCount: params.failureCount }),
      onSuccess: (_, params) => this.queryClient.invalidateQueries({ queryKey: queryKeys.users.byId(params.userId) }),
    })
  }

  downloadCalendarEvents(userId: number, startDate: Date, endDate: Date) {
    const fileName = `calendar-events-${userId}-${new Date().toISOString()}.json`
    return this.httpClient
      .get(`/api/users/${userId}/calendar-events`, {
        responseType: 'blob',
        params: {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        },
      })
      .pipe(
        tap((data) => {
          this.downloadsService.downloadData(data, fileName, 'json')
        }),
        map(() => void 0)
      )
  }
}
